import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, X } from 'lucide-react';
import type { RiskDynamicsSite } from './RiskDynamicsScatterChart';

type ScatterPoint = RiskDynamicsSite & {
    x: number;
    y: number;
    fillClass: string;
    strokeClass: string;
    highlighted: boolean;
};

type Props = {
    active?: boolean;
    payload?: Array<{ payload: ScatterPoint }>;
    onClose?: () => void;
};

function formatScore(value: number | null | undefined): string {
    if (value === null || value === undefined) return '—';
    return value.toFixed(2);
}

const METRIC_INDICATORS: Array<{
    key: keyof Pick<ScatterPoint, 'physicalRisk' | 'stateOfNature' | 'impactOnNature' | 'dependencies'>;
    label: string;
    indicatorClass: string;
    separator?: boolean;
}> = [
    { key: 'physicalRisk', label: 'Physical Risk Score:', indicatorClass: 'bg-divergent-red-amber-green-1' },
    { key: 'stateOfNature', label: 'State of Nature Score:', indicatorClass: 'bg-divergent-red-amber-green-4', separator: true },
    { key: 'impactOnNature', label: 'Impact on Nature:', indicatorClass: 'bg-divergent-red-amber-green-2' },
    { key: 'dependencies', label: 'Dependencies:', indicatorClass: 'bg-divergent-red-amber-green-3' },
];

export default function ScatterChartTooltip({ active, payload, onClose }: Props) {
    if (!active || !payload?.length) return null;

    const site = payload[0].payload;

    return (
        <div className='bg-white border border-border rounded-lg shadow-lg px-3 py-2 flex flex-col gap-2 w-[232px]'>
            <div className='flex items-start justify-between gap-2'>
                <p className='text-xs font-medium text-muted-foreground leading-4 tracking-wide'>{site.name}</p>
                {onClose && (
                    <button
                        onClick={onClose}
                        className='shrink-0 -mt-0.5 -mr-0.5 rounded-sm text-foreground/70 hover:text-foreground hover:bg-accent p-0.5 transition-colors'
                        aria-label='Close tooltip'
                    >
                        <X className='h-3.5 w-3.5' strokeWidth={2.5} />
                    </button>
                )}
            </div>

            {METRIC_INDICATORS.map(({ key, label, indicatorClass, separator }) => (
                <React.Fragment key={key}>
                    {separator && <div className='h-px bg-border w-full' />}
                    <div className='flex items-center justify-between w-full'>
                        <div className='flex items-center gap-2'>
                            <div className={`${indicatorClass} rounded-[2px] size-[10px] shrink-0`} />
                            <p className='text-xs text-foreground leading-4 tracking-wide whitespace-nowrap'>{label}</p>
                        </div>
                        <p className='text-xs font-medium text-foreground leading-4 tracking-wide whitespace-nowrap'>
                            {formatScore(site[key] as number | null)}
                        </p>
                    </div>
                </React.Fragment>
            ))}

            <Link
                to={`/site-nature-risk/${site.id}/overview`}
                className='flex items-center justify-center gap-2 px-2 py-[4px] rounded text-xs font-medium text-foreground leading-4 tracking-wide whitespace-nowrap hover:bg-accent transition-colors'
            >
                View site
                <ChevronRight className='size-[13px]' />
            </Link>
        </div>
    );
}
